import React, { useState, useEffect, useContext } from 'react'
import './DailyReport.css'
import { useParams, useNavigate } from 'react-router-dom'
import UserContext from '../context/UserContext'
import { Link } from 'react-router-dom'

import Navbar from "../components/Navbar"
import NavbarWhitelisted from '../components/NavbarWhitelisted'
import ReportCalendar from "../components/ReportCalendar"
import TocAccordionItem from "../components/TocAccoridonItem"
import { sortOrderName, sortByDate, getUniqueOutletObjects } from "../util/modelUtil"

import { API, graphqlOperation } from 'aws-amplify'
import { getFccReport, listFccOutlets, listFccReports, listFccCategories } from '../graphql/queries'
import { fetchArticlesByReportId } from '../util/graphqlCalls'

import { sortFccArticles } from './DailyReportFccUtil'


const DailyReport = () => {

  let { id } = useParams()
  const { isWhitelisted } = useContext(UserContext)
  const navigate = useNavigate()
  
  const [report, setReport] = useState({})
  const [articles, setArticles] = useState([])
  const [groupedArticles, setGroupedArticles] = useState([{}])
  const [outletList, setOutletList] = useState([])
  const [outletsInArticles, setOutletsInArticles] = useState([])
  const [categoryList, setCategoryList] = useState([{}])
  const [isLoading, setIsLoading] = useState(true)
  const [visibleContent, setVisibleContent] = useState({});

  
  const toggleContentVisibility = (articleId) => {
    setVisibleContent(prevState => ({
      ...prevState,
      [articleId]: !prevState[articleId]
    }))
  }

  /**
   * Fetch the report with the given id. The id is passed in the url.
   * Note: This app does not contain db table relationships, therefore we have
   *  to use a nested query: first get the report, 
   *  then get the article/report associations (join table),
   *  then get each article associated with the report.
   */
  const fetchReport = async (reportId) => {
    try {
      // Get categories
      // await fetchCategories()

      // Get report
      const reportResponse = await API.graphql(graphqlOperation(getFccReport, { id: reportId }))
      const report = reportResponse.data.getFccReport       
      const articles = await fetchArticlesByReportId(report.id)

      // Remove extra lines (Quill react component saves spaces as <p><br></p>, causing extra line breaks)
      articles.forEach((article) => {
        if (article.content) {
          article.content = article.content.replace(/\<p>\s*\<br\s*\/?\>\s*\<\/p\>/gi, '');
        }
        if (article.summary) {
          article.summary = article.summary.replace(/\<p>\s*\<br\s*\/?\>\s*\<\/p\>/gi, '');
        }
      })

      // Get outlets to match Article outletId 
      try {
        const outletResponse = await API.graphql(graphqlOperation(listFccOutlets, 
          { 
            filter: { 
              order: { gt: 0 },
            },
            limit: 200000 
          }
        ))

        let priorityOutlets = outletResponse.data.listFccOutlets.items
        priorityOutlets = priorityOutlets.filter((record) => !record._deleted)   
        priorityOutlets = sortOrderName(priorityOutlets, 'name')
        const [outletsInArticles, updatedArticles] = getUniqueOutletObjects(articles, priorityOutlets)
        
        // Sort articles
        const sortedArticles = sortFccArticles(updatedArticles, categoryList, outletsInArticles)

        // Group articles by category for the TOC
        const groupedArticles = sortedArticles.reduce((acc, article) => {
          const category = article.categoryName || 'Other'
          acc[category] = acc[category] || []
          acc[category].push(article)
          return acc
        }, {})

        // Order the grouped categories based on the sorted categories list
        const groupedArticlesForToc = categoryList.reduce((orderedAcc, category) => {
          if (groupedArticles[category.name]) {
            orderedAcc[category.name] = groupedArticles[category.name]
          }
          return orderedAcc
        }, {})
        
        // Add the 'Other' category at the end if it exists
        if (groupedArticles['Other']) {
          groupedArticlesForToc['Other'] = groupedArticles['Other'];
        }

        setOutletList(priorityOutlets)
        setOutletsInArticles(outletsInArticles)
        setArticles(updatedArticles)
        setGroupedArticles(groupedArticlesForToc)

      } catch (error) {
        console.error('Error fetching data', error)
      }
      
      setReport(report)

      // console.log('categoryList', categoryList)
      // console.log('report', report)
      // console.log('articles', articles)
  
    } catch (error) {
      console.error('Error fetching data', error)
    }
  }


  /**
   * Fetch the latest report. This is used when the id is not passed in the url.
   * Gets the latest published reports, sorts by most recent, and sets the id 
   * of first item.
   */
  const fetchLatestReport = async () => {
    let latestReportId = 0

    // NOTE: Using high query limit to retrieve a lot of reports to maintain order.
    // - Sort not working with graphql queryReportsByListReportsSortByCreatedAt
    // - Sort hack not working:
    //      let filterDate = new Date()
    //      filterDate.setDate(filterDate.getDate() - 50)
    //      filter: {publishedAt: {gt: filterDate}}
    const result = await API.graphql(graphqlOperation(listFccReports, {
      filter: {
        publishedAt: {gt: '0000-00-00T00:00:00Z'},  // filter out unpublished reports
      }, 
      limit: 4000
    }))
    if (result.data.listFccReports.items.length > 0) {
      const reports = sortByDate(result.data.listFccReports.items, 'publishedAt')
      latestReportId = reports[0].id
    }
    return latestReportId
  }

  const fetchCategories = async () => {
    try {
      const res = await API.graphql(
        graphqlOperation(listFccCategories)
      )
      let items = res.data.listFccCategories.items
      items = items.filter((record) => !record._deleted)

      // sort by order
      items.sort((a, b) => a.order - b.order);

      setCategoryList(items)
    } catch (error) {
      console.error("Error fetching categories:", error)
    }
  }

  const formatDate = (date, withTime=false) => {
    date = new Date(date)
    let formattedDate = date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit'
    })
    let [month, day, year] = formattedDate.split(' ')
    day = day.replace(',', '')

    let result = `${day} ${month} ${year}`

    if (withTime) {
      let formattedTime = date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      })
      result += ` - ${formattedTime}`
    }
  
    return result
  }

  const formatCalendarDate = () => {
    const date = new Date()
    let formattedDate = date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
    return `${formattedDate}`
  }

  const scrollToSection = (sectionId) => {
    const section = document.getElementById(sectionId)
    section.scrollIntoView({ behavior: 'smooth' })
  }

  
  useEffect(() => {
    const fetchData = async () => {
      await fetchCategories()
    }
    fetchData()
  }, [id])

  useEffect(() => {
    const fetchData = async () => {
      if (id) {
        await fetchReport(id)
      } else {
        const latestReportId = await fetchLatestReport()
        await fetchReport(latestReportId)
      }
      setIsLoading(false)
    }
    fetchData()
  }, [id, categoryList])


  const todayClickHandler = (e) => {
    navigate ('/home/daily')
  }


  return (
    <>
    <span id="page-top"></span>
    { isWhitelisted ? <NavbarWhitelisted /> : <Navbar />}

    {isLoading ? (
      <span></span>
    ) : (
      <div>
        <div className="container-fluid banner-no-space text-center text-white">
          {report.title}
        </div>

        <div className="container-fluid vh-100">
          <div className="row row-offcanvas row-offcanvas-left vh-100">
            
            {/* <!-- Sidebar (TOC) --> */}
            <div id="sidebar" className="col-md-3 col-lg-2 pe-0 d-lg-block sidebar-offcanvas custom-max-height overflow-auto ">
              <div
                className="nav sidebar-toc flex-column mx-0"
                data-bs-accordion="true"
              >
                <h5 className="sidebar-toc-title py-2 mx-0 text-white text-center">
                  Topics
                </h5>

                <nav className="nav" id="left-nav" style={{width: '100%'}}>
                  {Object.keys(groupedArticles).map((category, index) => (
                    <div key={index} className="sidebar-toc-category-title">
                      <a href={`#${category}`}>{category}</a>
                    </div>
                  ))}
                </nav>

                <br/>
                <h5 className="sidebar-toc-title py-2 mx-0 text-white text-center">
                  Info
                </h5>

                <div className="report-info">
                  <h6><strong>Published</strong></h6>
                  <p>{formatDate(report.publishedAt, true)}</p>

                  <h6><strong>Print Friendly Version</strong></h6>
                  <p><a href={report.wordUrl}>{report.title}</a></p>
                  
                  <h6><strong>Published By</strong></h6>
                  <p><a href="mailto:mediaanalyst@barbaricum.com">Barbaricum, LLC</a></p>
                  <p>To receive this briefing via your FCC email address, contact the Office of Public Affairs at <a href="mailto:MediaRelations@fcc.gov">MediaRelations@fcc.gov</a>.</p>
                </div>
              </div>
            </div>

            
            {/* <!-- Main articles --> */}
            <div className="col-md-6 col-lg-7 py-4 px-5 " id="articles">
              <main id="main" className="row overflow-auto h-100 custom-max-height pb-5">
                <div className="report">

                  {Object.entries(groupedArticles).map(([category, articles]) => (
                    <section key={category} id={category} name={category} className="category">
                      <h2 className="article-category">{category}</h2>
                      <hr/>
                      {articles.map(article => (
                        <article key={article.id}>

                          {article.categoryName !== 'Social Media Summary'&& (
                          <header>
                              <h3 className="mb-0 mt-4 article-title">{article.title}: {article.outletName && article.outletName.toUpperCase()}</h3>
                              <p>{formatDate(article.publishedAt)}{article.author && `, ${article.author}`}</p>
                          </header>
                          )}
                          <div className="content">
                            {article.summary && <p dangerouslySetInnerHTML={{ __html: article.summary }} />}
                            {visibleContent[article.id] && article.content && (
                            <p className="full-text-panel" dangerouslySetInnerHTML={{ __html: article.content }} />
                            )}
                          </div>
                          <div>
                            <p>
                              <a 
                                alt="View original article on source website" 
                                style={{marginBottom: '6px'}} 
                                className="btn btn-success btn-sm" 
                                href={article.url} target="_blank"
                              >
                                View Original
                              </a>

                              &nbsp;&nbsp;

                              <a 
                                alt="Show full article text" 
                                style={{marginBottom: '6px'}} 
                                className="btn btn-success btn-sm" 
                                onClick={() => toggleContentVisibility(article.id)}
                              >
                                {visibleContent[article.id] ? 'Hide Full Text' : 'Show Full Text'}
                              </a>
                            </p>
                          </div>
                        </article>
                      ))}

                      {/* <a href="#page-top" className="anchor-link btn btn-outline-success" data-anchor="page-top" aria-label="Return to Top" title="Return to Top">
                        <i className="fas fa-fas fa-arrow-up mx-2"></i>Return to Top
                      </a> */}
                      <br/>
                    </section>
                  ))}
                  
                </div>
              </main>
            </div>

            {/* <!-- Calendar --> */}
            <div className="right-navbar col-md-3 col-lg-3 text-center">
              <div>
                <div className="calendar-container">
                  <ReportCalendar />
                </div>
                <div className="report-date">
                  <a href="#" onClick={todayClickHandler}>Today - {formatCalendarDate()}</a>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    )} 
    </>
  )
}

export default DailyReport
